// Public capacity-tier pricing (capacity_tier_v2). The BACKEND billing catalog
// (billing_plans / billing_plan_features / billing_overage_rules) is the single
// source of truth; these constants mirror it for client-side marketing and the
// interactive ROI calculator only. Amounts here are in whole USD dollars/month.
// Do not let these diverge from the backend catalog — see migration
// 273_capacity_tier_pricing_restore.sql. Premium add-ons (Punch AI, Predictive
// Forecasting, Punch Learning, Punch Mentor) are priced and sold separately and
// are intentionally not represented here.

export type PricingPlanId = 'free' | 'starter' | 'growth' | 'operations';

export interface PricingPlan {
  id: PricingPlanId;
  name: string;
  /** Base subscription price in whole USD dollars per month. */
  basePrice: number;
  /** Included active worker/user seats; null means "no fixed cap presented". */
  includedWorkers: number | null;
  /** Included locations; null means "no fixed cap presented". */
  includedLocations: number | null;
  /** Additional active worker price in whole USD dollars per worker/month. */
  overagePrice: number;
  /** Additional location price in whole USD dollars per location/month. */
  locationOveragePrice: number;
}

export const PRICING_PLANS: PricingPlan[] = [
  {
    id: 'free',
    name: 'Free',
    basePrice: 0,
    includedWorkers: 10,
    includedLocations: 1,
    overagePrice: 0,
    locationOveragePrice: 0,
  },
  {
    id: 'starter',
    name: 'Starter',
    basePrice: 1000,
    includedWorkers: 100,
    includedLocations: 2,
    overagePrice: 10,
    locationOveragePrice: 150,
  },
  {
    id: 'growth',
    name: 'Growth',
    basePrice: 3000,
    includedWorkers: 300,
    includedLocations: 5,
    overagePrice: 9,
    locationOveragePrice: 150,
  },
  {
    id: 'operations',
    name: 'Operations',
    basePrice: 8000,
    includedWorkers: 1000,
    includedLocations: 15,
    overagePrice: 8,
    locationOveragePrice: 150,
  },
];

/**
 * Pick the smallest plan whose included worker capacity covers the count.
 * Falls back to the largest public plan (Operations) above its included cap.
 */
export function selectPlanForWorkerCount(workerCount: number): PricingPlan {
  for (const plan of PRICING_PLANS) {
    if (plan.includedWorkers === null || workerCount <= plan.includedWorkers) {
      return plan;
    }
  }
  return PRICING_PLANS[PRICING_PLANS.length - 1];
}

/**
 * Estimate the monthly cost for a worker/location count under the capacity-tier
 * model: base subscription + worker overage + location overage. This mirrors
 * the backend calculator; the backend remains authoritative for billing.
 */
export function calculateMonthlyCost(workerCount: number, locationCount = 1): {
  plan: PricingPlan;
  basePrice: number;
  overagePrice: number;
  locationOveragePrice: number;
  totalPrice: number;
} {
  const plan = selectPlanForWorkerCount(workerCount);
  const workerOverCount = plan.includedWorkers === null ? 0 : Math.max(0, workerCount - plan.includedWorkers);
  const locationOverCount = plan.includedLocations === null ? 0 : Math.max(0, locationCount - plan.includedLocations);
  const overagePrice = workerOverCount * plan.overagePrice;
  const locationOveragePrice = locationOverCount * plan.locationOveragePrice;
  return {
    plan,
    basePrice: plan.basePrice,
    overagePrice,
    locationOveragePrice,
    totalPrice: plan.basePrice + overagePrice + locationOveragePrice,
  };
}
